import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { useCompanyAdminInfo } from "@admin/hooks/useCompanyAdminInfo";
import { useCurrentUserGroups } from "@admin/hooks/useCurrentUserGroups";
import { Company, updateCompany } from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { EditAdminPasswordModal } from "./Modals/EditAdminPasswordModal";

export function AdminInfo() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isEditAdminPasswordModalOpen, setIsEditAdminPasswordModalOpen] =
    useState(false);
  const { companyAdminInfo, isPending } = useCompanyAdminInfo();
  const { isSuperAdmin } = useCurrentUserGroups();

  const adminInfoSchema = z.object({
    firstName: z
      .string()
      .min(1, { message: t("common.First name is required") }),
    lastName: z.string().min(1, { message: t("common.Last name is required") }),
    userName: z.string().min(1, { message: t("common.Username is required") }),
    email: z.string().email({ message: t("common.Invalid email address") }),
    phone: z.string().min(1, { message: t("common.Phone number is required") }),
  });

  type AdminInfoForm = z.infer<typeof adminInfoSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AdminInfoForm>({
    resolver: zodResolver(adminInfoSchema),
  });

  useEffect(() => {
    if (companyAdminInfo) {
      reset({
        firstName: companyAdminInfo.firstName,
        lastName: companyAdminInfo.lastName,
        userName: companyAdminInfo.userName,
        email: companyAdminInfo.email,
        phone: companyAdminInfo.phone,
      });
    }
  }, [companyAdminInfo, reset]);

  const onSubmit = (data: AdminInfoForm) => {
    if (!companyAdminInfo) return;

    const updatedcompanyAdminInfo: Company = {
      ...companyAdminInfo,
      ...data,
    };
    mutation.mutate(updatedcompanyAdminInfo);
  };
  const mutation = useMutation({
    mutationFn: updateCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [isSuperAdmin ? "companyInfo" : "userCompany"],
      });
      reset();
      toast.success(t("common.The changes have been saved"), {
        duration: 4000,
      });
    },
    onError: (err) => {
      toast.error(t("common.Unable to save the changes"), {
        duration: 4000,
      });
    },
  });

  return (
    <div className="card pb-2.5">
      <div className="card-header" id="basic_settings">
        <div className="flex flex-row gap-10">
          <h3 className="card-title leading-8">{t("settings.Admin Info")}</h3>
          <i className="ki-outline ki-information-3 text-[#8D4F61] text-2xl" />
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="card-body px-0 grid gap-5">
          <div className="w-full px-7.5">
            <div className="flex items-baseline flex-wrap lg:flex-nowrap gap-2.5">
              <label className="form-label flex items-center gap-1 max-w-56 !font-normal">
                {t("settings.Admin First Name")}
              </label>
              {isPending || mutation.isPending ? (
                <div className="h-2 bg-slate-200 rounded w-[30%]"></div>
              ) : (
                <div className="d-flex flex-col w-full">
                  <Input
                    placeholder={t("settings.Edit first name")}
                    {...register("firstName")}
                  />
                  <ErrorMessage message={errors.firstName?.message as string} />
                </div>
              )}
            </div>
          </div>
          <div className="w-full border-t 1px" />
          <div className="w-full px-7.5">
            <div className="flex items-baseline flex-wrap lg:flex-nowrap gap-2.5">
              <label className="form-label flex items-center gap-1 max-w-56 !font-normal">
                {t("settings.Admin Last Name")}
              </label>
              {isPending || mutation.isPending ? (
                <div className="h-2 bg-slate-200 rounded w-[30%]"></div>
              ) : (
                <div className="d-flex flex-col w-full">
                  <Input
                    placeholder={t("settings.Edit last name")}
                    {...register("lastName")}
                  />
                  <ErrorMessage message={errors.lastName?.message as string} />
                </div>
              )}
            </div>
          </div>
          <div className="w-full border-t 1px" />
          <div className="w-full px-7.5">
            <div className="flex items-baseline flex-wrap lg:flex-nowrap gap-2.5">
              <label className="form-label flex items-center gap-1 max-w-56 !font-normal">
                {t("settings.Admin Username")}
              </label>
              {isPending || mutation.isPending ? (
                <div className="h-2 bg-slate-200 rounded w-[30%]"></div>
              ) : (
                <div className="d-flex flex-col w-full">
                  <Input
                    placeholder={t("settings.Edit username")}
                    {...register("userName")}
                  />
                  <ErrorMessage message={errors.userName?.message as string} />
                </div>
              )}
            </div>
          </div>
          <div className="w-full border-t 1px" />
          <div className="w-full px-7.5">
            <div className="flex items-baseline flex-wrap lg:flex-nowrap gap-2.5">
              <label className="form-label flex items-center gap-1 max-w-56 !font-normal">
                {t("settings.Admin Email")}
              </label>
              {isPending || mutation.isPending ? (
                <div className="h-2 bg-slate-200 rounded w-[30%]"></div>
              ) : (
                <div className="d-flex flex-col w-full">
                  <Input
                    placeholder={t("settings.Edit email")}
                    {...register("email")}
                  />
                  <ErrorMessage message={errors.email?.message as string} />
                </div>
              )}
            </div>
          </div>
          <div className="w-full border-t 1px" />
          <div className="w-full px-7.5">
            <div className="flex items-baseline flex-wrap lg:flex-nowrap gap-2.5">
              <label className="form-label flex items-center gap-1 max-w-56 !font-normal">
                {t("settings.Admin Phone")}
              </label>
              {isPending || mutation.isPending ? (
                <div className="h-2 bg-slate-200 rounded w-[30%]"></div>
              ) : (
                <div className="d-flex flex-col w-full">
                  <Input
                    placeholder={t("settings.Edit phone")}
                    maxLength={14}
                    {...register("phone")}
                  />
                  <ErrorMessage message={errors.phone?.message as string} />
                </div>
              )}
            </div>
          </div>
          <div className="w-full border-t 1px" />
          <div className="w-full px-7.5">
            <div className="flex items-baseline justify-between flex-wrap lg:flex-nowrap gap-2.5">
              <label className="form-label flex items-center gap-1 max-w-56 !font-normal">
                {t("common.Password")}
              </label>
              <Input
                type="password"
                variant="outlined"
                defaultValue="****************"
              />
              <i
                className="ki-filled text-blue-500 ki-notepad-edit text-xl cursor-pointer"
                onClick={() => setIsEditAdminPasswordModalOpen(true)}
              ></i>
            </div>
          </div>
          <div className="w-full border-t 1px" />
          <div className="flex justify-end pt-2.5 px-7.5 gap-2.5">
            <Button
              variant="secondary"
              type="button"
              onClick={() => reset(companyAdminInfo)}
            >
              {t("common.Discard Changes")}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {t("common.Save Changes")}
            </Button>
          </div>
        </div>
      </form>
      <EditAdminPasswordModal
        open={isEditAdminPasswordModalOpen}
        onClose={() => setIsEditAdminPasswordModalOpen(false)}
        companyData={companyAdminInfo as Company}
      />
    </div>
  );
}
